import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  AlignHorizontalJustifyStart,
  AlignVerticalJustifyStart,
  Archive,
  Copy,
  FileDown,
  FileUp,
  History,
  LayoutGrid,
  Monitor,
  Pencil,
  Redo2,
  Save,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  NETWORK_LIFECYCLE_LABELS_UK,
  NETWORK_LIFECYCLE_ORDER,
  normalizeNetworkLifecycle,
  type NetworkLifecycle,
} from '@ukrop-nm/shared';
import { UkropTip } from '../tooltips/DiagramTooltipContext';
import { RibbonDropMenu } from './RibbonDropMenu';

function RibbonGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ribbon-group">
      <div className="ribbon-group-body">{children}</div>
      <div className="ribbon-group-label">{label}</div>
    </div>
  );
}

function RibbonBtn({
  label,
  icon,
  onClick,
  disabled,
  active,
  prominent,
  tip,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  prominent?: boolean;
  tip?: string;
}) {
  const className = clsx(
    'ribbon-btn',
    active && 'is-active',
    prominent && 'is-prominent',
    disabled && 'is-disabled',
  );

  return (
    <UkropTip text={tip ?? label}>
      <button type="button" className={className} disabled={disabled} onClick={onClick}>
        <span className="ribbon-btn-icon">{icon}</span>
        <span className="ribbon-btn-text">{label}</span>
      </button>
    </UkropTip>
  );
}

export function EditorRibbon({
  mode,
  setMode,
  canEdit,
  onSave,
  onUndo,
  onRedo,
  onDelete,
  onCopy,
  onAlignLeft,
  onAlignTop,
  onExportJson,
  onExportJpeg,
  onExportSvg,
  onExportPdf,
  onExportDrawio,
  onExportCsv,
  onGenerateProject,
  exporting,
  lifecycle,
  onLifecycle,
  onImportDrawio,
  onImportJson,
  onImportCsv,
  paletteOpen,
  onTogglePalette,
  onOpenVersions,
}: {
  mode: 'edit' | 'monitor';
  setMode: (m: 'edit' | 'monitor') => void;
  canEdit: boolean;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onAlignLeft: () => void;
  onAlignTop: () => void;
  onExportJson: () => void;
  onExportJpeg: () => void;
  onExportSvg: () => void;
  onExportPdf: () => void;
  onExportDrawio: () => void;
  onExportCsv: () => void;
  onGenerateProject: () => void;
  exporting?: boolean;
  lifecycle: NetworkLifecycle;
  onLifecycle: (value: NetworkLifecycle) => void;
  onImportDrawio: (file: File) => void;
  onImportJson: (file: File) => void;
  onImportCsv: (file: File) => void;
  paletteOpen: boolean;
  onTogglePalette: () => void;
  onOpenVersions: () => void;
}) {
  return (
    <div className="editor-ribbon" role="toolbar" aria-label="Стрічка редактора">
      <RibbonGroup label="Режим">
        <RibbonBtn
          label="Редактор"
          icon={<Pencil size={18} strokeWidth={1.75} />}
          active={mode === 'edit'}
          onClick={() => setMode('edit')}
        />
        <RibbonBtn
          label="Моніторинг"
          icon={<Monitor size={18} strokeWidth={1.75} />}
          active={mode === 'monitor'}
          onClick={() => setMode('monitor')}
        />
      </RibbonGroup>

      <RibbonGroup label="Файл">
        {mode === 'edit' ? (
          <RibbonBtn
            label="Зберегти"
            icon={<Save size={18} strokeWidth={1.75} />}
            prominent
            onClick={onSave}
          />
        ) : null}
        <RibbonDropMenu
          label="Імпорт"
          icon={<FileUp size={18} strokeWidth={1.75} />}
          tip="Імпорт у схему"
          disabled={!canEdit}
          items={[
            {
              id: 'drawio',
              label: 'draw.io (.drawio, .xml, .png, .svg)',
              hint: 'PNG/SVG лише якщо diagrams.net вбудував XML',
              accept: '.drawio,.xml,.png,.svg,application/xml,text/xml,image/png,image/svg+xml',
            },
            {
              id: 'json',
              label: 'JSON схеми',
              hint: 'Раніше експортований diagram.json',
              accept: '.json,application/json',
            },
            {
              id: 'csv',
              label: 'CSV реєстри (.csv, .zip)',
              hint: 'equipment.csv та links.csv',
              accept: '.csv,.zip,text/csv,application/zip',
            },
          ]}
          onPickFile={(id, file) => {
            if (id === 'drawio') onImportDrawio(file);
            if (id === 'json') onImportJson(file);
            if (id === 'csv') onImportCsv(file);
          }}
        />
        <RibbonDropMenu
          label="Експорт"
          icon={<FileDown size={18} strokeWidth={1.75} />}
          tip="Експорт схеми або документів"
          disabled={exporting}
          items={[
            {
              id: 'jpeg',
              label: 'Малюнок JPEG',
              hint: 'Знімок топології',
            },
            {
              id: 'svg',
              label: 'Топологія SVG',
              hint: 'Світла схема без рамки',
            },
            {
              id: 'pdf',
              label: 'PDF опис',
              hint: 'Технічний опис мережі',
            },
            {
              id: 'json',
              label: 'JSON схеми',
              hint: 'Файл для повторного імпорту',
            },
            {
              id: 'drawio',
              label: 'draw.io (.drawio)',
              hint: 'Нестиснений mxfile для diagrams.net',
            },
            {
              id: 'csv',
              label: 'CSV реєстри',
              hint: 'equipment.csv + links.csv у ZIP',
            },
          ]}
          onSelect={(id) => {
            if (id === 'jpeg') onExportJpeg();
            if (id === 'svg') onExportSvg();
            if (id === 'pdf') onExportPdf();
            if (id === 'json') onExportJson();
            if (id === 'drawio') onExportDrawio();
            if (id === 'csv') onExportCsv();
          }}
        />
        <RibbonBtn
          label="Генерація"
          tip="ZIP проєкту: JPEG, SVG, PDF, JSON, draw.io, CSV. Працює для запланованої, проєктованої та вже монітореної мережі"
          icon={<Archive size={18} strokeWidth={1.75} />}
          disabled={exporting}
          onClick={onGenerateProject}
        />
      </RibbonGroup>

      <RibbonGroup label="Проєкт">
        <UkropTip text="Стадія проєкту (коротка назва). Повний опис стадії — у PDF та на кресленні.">
          <select
            className="ribbon-select ribbon-select-lifecycle"
            value={normalizeNetworkLifecycle(lifecycle)}
            disabled={!canEdit}
            onChange={(e) => onLifecycle(e.target.value as NetworkLifecycle)}
          >
            {NETWORK_LIFECYCLE_ORDER.map((id) => (
              <option key={id} value={id}>
                {NETWORK_LIFECYCLE_LABELS_UK[id]}
              </option>
            ))}
          </select>
        </UkropTip>
      </RibbonGroup>

      <RibbonGroup label="Буфер">
        <RibbonBtn
          label="Скасувати"
          icon={<Undo2 size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onUndo}
        />
        <RibbonBtn
          label="Повторити"
          icon={<Redo2 size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onRedo}
        />
        <RibbonBtn
          label="Копія"
          icon={<Copy size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onCopy}
        />
        <RibbonBtn
          label="Видалити"
          icon={<Trash2 size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onDelete}
        />
      </RibbonGroup>

      <RibbonGroup label="Упорядкувати">
        <RibbonBtn
          label="Ліворуч"
          icon={<AlignHorizontalJustifyStart size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onAlignLeft}
        />
        <RibbonBtn
          label="Вгору"
          icon={<AlignVerticalJustifyStart size={18} strokeWidth={1.75} />}
          disabled={!canEdit}
          onClick={onAlignTop}
        />
        <RibbonBtn
          label="Палітра"
          icon={<LayoutGrid size={18} strokeWidth={1.75} />}
          active={paletteOpen}
          disabled={!canEdit}
          onClick={onTogglePalette}
        />
        <RibbonBtn
          label="Версії"
          icon={<History size={18} strokeWidth={1.75} />}
          onClick={onOpenVersions}
        />
      </RibbonGroup>
    </div>
  );
}
